import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { ModalState, ProductCategoryForm, ProductCategory } from '../types/system-parameters.types';

interface ProductCategoryModalProps {
  modalState: ModalState;
  onClose: () => void;
  onSave: (formData: ProductCategoryForm, isEdit?: boolean, itemId?: string) => Promise<void>;
  totalCategories?: number;
}

export const ProductCategoryModal: React.FC<ProductCategoryModalProps> = ({
  modalState,
  onClose,
  onSave,
  totalCategories = 1
}) => {
  const [formData, setFormData] = useState<ProductCategoryForm>({
    name: '',
    code: '',
    description: '',
    displayOrder: 1
  });
  const [loading, setLoading] = useState(false);

  // Resetear formulario cuando se abra en modo crear o cuando cambie el item
  useEffect(() => {
    if (modalState.isOpen) {
      if (modalState.mode === 'edit' && modalState.item) {
        const category = modalState.item as ProductCategory;
        setFormData({
          name: category.additionalData.name,
          code: category.additionalData.code,
          description: category.additionalData.description,
          displayOrder: category.additionalData.displayOrder
        });
      } else {
        setFormData({
          name: '',
          code: '',
          description: '',
          displayOrder: totalCategories + 1
        });
      }
    }
  }, [modalState.isOpen, modalState.mode, modalState.item]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim() || !formData.code.trim()) {
      return;
    }

    setLoading(true);
    try {
      await onSave(
        formData,
        modalState.mode === 'edit',
        modalState.item?.id
      );
      onClose();
    } catch (error) {
      console.error('Error al guardar categoría:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCodeGeneration = () => {
    if (formData.name) {
      const code = formData.name
        .toUpperCase()
        .replace(/[^A-Z0-9\s]/g, '')
        .replace(/\s+/g, '_')
        .substring(0, 20);
      setFormData(prev => ({ ...prev, code }));
    }
  };

  return (
    <Dialog open={modalState.isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {modalState.mode === 'edit' ? 'Editar Categoría' : 'Nueva Categoría'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Nombre *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, name: e.target.value }));
              }}
              onBlur={handleCodeGeneration}
              placeholder="Ej: Sandwiches"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="code">Código *</Label>
            <Input
              id="code"
              value={formData.code}
              onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
              placeholder="Ej: SANDWICHES"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="displayOrder">Orden de visualización</Label>
            <Select
              value={formData.displayOrder.toString()}
              onValueChange={(value) => setFormData(prev => ({ ...prev, displayOrder: parseInt(value) }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar orden" />
              </SelectTrigger>
              <SelectContent>
                {Array.from({ length: modalState.mode === 'create' ? totalCategories + 1 : totalCategories }, (_, i) => i + 1).map(n => (
                  <SelectItem key={n} value={n.toString()}>
                    {n}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Descripción</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Descripción de la categoría"
              rows={3}
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={!formData.name.trim() || !formData.code.trim() || loading}
              className="bg-macartur-primary"
            >
              {loading ? 'Guardando...' : modalState.mode === 'edit' ? 'Actualizar' : 'Crear'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};